import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTruck, faStore, faUser, faMapMarkerAlt, faMap, faCheckCircle, faTimesCircle, faBox, faPallet, faMotorcycle, faHandHolding, faBolt, faClock, faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '../../../_services/user.service';
import { ShippingConfigService } from '../../../_services/shipping-config.service';
import { CustomerService, CustomerSearchResult } from '../../../_services/customer.service';
import {
  ArticleDisposition,
  PackageSpeed,
  PickpointStation,
  PickpointStationShort,
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
  ShippingConfigType,
} from '../../../_models/shipping-config.interface';
import { PickpointMapPickerComponent } from './pickpoint-map-picker.component';

interface TechnicianOption { id: number; name: string; }
interface WarehouseOption { id: number; label: string; }

/**
 * Pure Formular-Komponente fuer das Erstellen/Bearbeiten einer Versandkonfiguration.
 *
 * Hat keine eigene HTTP-Persistenz: Context wird vom Parent als Input geliefert,
 * die Form emittiert beim Speichern die Datenform `ShippingConfigFormData`. Der
 * Parent ist fuer das HTTP-POST zustaendig (Single-Op-API, Bulk-API oder
 * beliebigen anderen Endpunkt).
 *
 * Techniker-Liste und Techniker-Lager werden intern ueber den `UserService`
 * geladen — diese Listen haengen vom `projectId`- bzw. `targetUserId`-Input ab
 * und sind in allen Einsatzkontexten gleich.
 */
@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, FontAwesomeModule, PickpointMapPickerComponent],
  selector: 'hawk-shipping-config-form',
  templateUrl: './shipping-config-form.component.html',
})
export class ShippingConfigFormComponent implements OnInit, OnChanges {
  // Platzhalter-/System-User (Bentomax Lager = 709, nicht vergeben = 283), die
  // nicht als Ziel-Techniker waehlbar sind. Spiegelt
  // ShippingConfig::PLACEHOLDER_TECHNICIAN_IDS im Backend.
  static readonly PLACEHOLDER_TECHNICIAN_IDS = [709, 283];

  /** Bestehende Config fuer Edit-Modus; null oder undefined fuer Create-Modus. */
  @Input() initialConfig: ShippingConfig | null = null;
  /** Kontextinfos (Rueckadresse-Flag, Filialname) — vom Parent geladen. */
  @Input() context: ShippingConfigContext | null = null;
  /** Store-ID des Tickets — entscheidet ob `store_shipping` erlaubt ist. */
  @Input() ticketStoreId?: number;
  /** Fuer den PickPoint-Kartenpicker: Mittelpunkt. */
  @Input() ticketLat?: number;
  @Input() ticketLng?: number;
  /** Stichtag fuer Station-Aktivitaet (optional). */
  @Input() referenceDate?: string;
  /** Project-ID zum Filtern der Technikerliste (optional). */
  @Input() projectId?: number;
  /** Vorausgewaehlter Techniker (z.B. Operations-Techniker bei Neu-Anlage). */
  @Input() operationTechnicianId?: number;
  /** Name des Auftragstechnikers — fuer die Anzeige im Regel-Modus ("vom Auftrag"). */
  @Input() operationTechnicianName?: string;
  /** Ueberschrift des Cards. `null` blendet sie aus. */
  @Input() heading: string | null = 'Versandkonfiguration';
  /** Primaer-Button-Label. */
  @Input() saveLabel = 'Speichern';
  /** Hinweistext unter dem Versanddatum. */
  @Input() shippingDateHint = 'Leer lassen für Versand am Auftragsdatum.';
  /**
   * true, wenn dieser Config bereits mind. ein Staging-Artikel zugeordnet ist.
   * Dann ist Kunden-Rueckversand gesperrt — Staging-Artikel koennen nicht ueber
   * einen Kunden-Rueckversand laufen (siehe canAssign/COVERING_TYPES in der
   * Versand-Matrix). Erst die Staging-Zuordnung loesen.
   */
  @Input() stagingArticlesAssigned = false;

  @Output() save = new EventEmitter<ShippingConfigFormData>();
  @Output() cancel = new EventEmitter<void>();

  // Icons
  iconMap = faMap;
  iconCheck = faCheckCircle;
  iconTimes = faTimesCircle;
  iconClock = faClock;
  iconInfo = faInfoCircle;
  iconWarn = faExclamationTriangle;

  // State
  showMapPicker = signal(false);
  selectedType = signal<ShippingConfigType>('pickpoint');
  selectedStation = signal<PickpointStationShort | null>(null);
  shipmentMethod = signal<ShipmentMethod | null>('parcel');
  packageSpeed = signal<PackageSpeed | null>('standard');
  articleDisposition = signal<ArticleDisposition>('technician_transfer');
  shippingDate = signal<string>('');
  // Pickup-Datum nur fuer PickPoint relevant. Format: `YYYY-MM-DDTHH:mm` fuer
  // <input type="datetime-local">. Beim Speichern wird ein leeres Feld als
  // null an den Server geschickt (Backend setzt dann den Default beim Create
  // bzw. behaelt den DB-Wert beim Update — siehe Controller).
  pickupDate = signal<string>('');
  // Frueheste-Versand-Zeitpunkt fuer Staging-Pakete (Verzoegerter Versand).
  // Leer = keine Verzoegerung. Format wie pickupDate ("YYYY-MM-DDTHH:mm").
  stagingShipReadyAt = signal<string>('');

  // Regel "Ziel-Techniker vom Auftrag": true = kein fester Techniker, Auflösung
  // dynamisch aus dem Auftragstechniker. Default AN bei Neu-Anlage der Typen mit
  // Techniker-Umbuchung (Regel als Standard).
  targetUserFromOperation = signal<boolean>(false);

  // Datums-Modus pro Kanal: 'fixed' = festes Datum, 'offset' = Regel "X Werktage
  // vor Auftragsdatum". Normaler Versand: Default 'offset' (Regel als Standard).
  // Staging hat zusaetzlich 'standard' = keine Verzoegerung (Versand nach
  // KW-Zuordnung durch die Staging-Config) und ist der Default.
  shippingDateMode = signal<'fixed' | 'offset'>('fixed');
  // Initialwert nur als Vor-Render-Fallback; der echte Default kommt aus dem
  // Context (Gruppen-Vorlauf) bzw. dem gespeicherten Wert — siehe
  // applyInitialConfig / defaultShippingOffsetDays.
  shippingOffsetDays = signal<number>(12);
  stagingShipMode = signal<'fixed' | 'offset' | 'standard'>('standard');
  stagingOffsetDays = signal<number>(5);

  // Standard-Vorlauf fuer den Versand-Vorschlag (Termin minus N WERKTAGE).
  // Spiegelt ShippingConfig::DEFAULT_SHIPPING_DELAY_DAYS im Backend. PickPoint:
  // Versand- und Staging-Datum 5 Werktage vor dem Abholzeitpunkt (Sonntage werden
  // rausgerechnet; Feiertage spaeter).
  private readonly STAGING_DELAY_DAYS = 5;
  // Vorlauf des „normalen" Versanddatums fuer Nicht-PickPoint-Typen (Filial-/
  // Techniker-/Kunden-Rueckversand) im FEST-Datum-Modus: 14 KALENDERtage vor dem
  // Auftragsdatum (= genau 2 Wochen). Staging-Verzoegerung bleibt bei diesen
  // Typen leer (0 Tage).
  private readonly DEFAULT_LEAD_DAYS = 14;
  // Fallback-Vorlauf der OFFSET-Regel ("X Werktage vor Auftrag"), falls der
  // Context (Gruppen-Default) noch nicht geladen ist. 12 WERKTAGE ≈ 2 Wochen
  // (14 Kalendertage − 2 Sonntage). Authoritativ ist GROUP_SHIPPING_OFFSET_DAYS
  // im Backend (default_shipping_offset_days im Context).
  private readonly DEFAULT_OFFSET_WORKING_DAYS = 12;

  technicians = signal<TechnicianOption[]>([]);
  technicianWarehouses = signal<WarehouseOption[]>([]);
  loadingWarehouses = signal(false);

  // Signals fuer Felder, die in `canSave` einfliessen — sonst triggert
  // ngModel-Tippen kein Recompute (Button bleibt disabled).
  targetUserId = signal<number | null>(null);
  pmCode = signal('');

  // Form-Felder ohne Einfluss auf `canSave` (direkt mit [(ngModel)] gebunden)
  targetWarehouseId: number | null = null;
  // Technikerversand-Adresse: legt das Buero fest, der Lagerist kann in der
  // LagerApp dann nicht mehr waehlen. Default 'warehouse' (Lageradresse).
  technicianShipTarget = signal<'private' | 'warehouse'>('warehouse');

  // Erweiterter Bereich (collapsible). Enthaelt Ziel-Techniker, Ziel-Lager,
  // Notizen und ggf. den PM-Code (sofern er erfolgreich vom System-User
  // vorbelegt werden konnte). Default zugeklappt — beim Bearbeiten einer
  // bestehenden Config wird automatisch ausgeklappt, wenn dort Werte stehen.
  showAdvancedSection = signal<boolean>(false);

  // True, wenn der PM-Code aus dem System-User (ID 1) erfolgreich vorbelegt
  // wurde — dann landet der PM-Code im erweiterten Bereich, weil er nur
  // selten manuell ueberschrieben werden muss. Andernfalls bleibt er sichtbar
  // (und Pflicht), damit der User ihn manuell eintragen kann.
  pmCodeAutoLoaded = signal<boolean>(false);

  canOpenMap = computed(() =>
    this.ticketLat != null && this.ticketLng != null &&
    !Number.isNaN(this.ticketLat) && !Number.isNaN(this.ticketLng),
  );

  showTechnicianSelector = computed(() => {
    const type = this.selectedType();
    if (type === 'pickpoint' || type === 'technician_shipping') return true;
    if (type === 'store_shipping') return this.articleDisposition() === 'technician_transfer';
    return false;
  });

  // Auftragstechniker ist ein Platzhalter-/System-User (Bentomax Lager 709 /
  // nicht vergeben 283) → im Regel-Modus ("vom Auftrag") loest sich kein echter
  // Ziel-Techniker auf (Backend: primary_technician = nil). Die LagerApp warnt
  // dann und blockiert den Versand, bis am Auftrag ein echter Techniker steht.
  // Getter (kein computed), weil die Inputs ausserhalb des Signal-Graphen liegen.
  get operationTechnicianIsPlaceholder(): boolean {
    const id = this.operationTechnicianId;
    return id != null && ShippingConfigFormComponent.PLACEHOLDER_TECHNICIAN_IDS.includes(id);
  }

  canSave = computed(() => {
    if (this.shipmentMethod() == null) return false;
    if (this.shipmentMethod() === 'parcel' && this.packageSpeed() == null) return false;

    // PM-Code ist Pflicht bei PickPoint.
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) return false;

    switch (this.selectedType()) {
      case 'pickpoint':
        // Im Regel-Modus (Techniker vom Auftrag) ist kein fester Techniker noetig.
        if (this.selectedStation() == null || (this.targetUserId() == null && !this.targetUserFromOperation())) return false;
        // Abholzeit muss innerhalb Aktivierungszeitraum + Wochentags-Oeffnungs-
        // zeiten der gewaehlten Station liegen — selbe Logik wie im
        // Sonderversand-Popup.
        return this.pickupValidityError() == null;
      case 'store_shipping':
        if (this.ticketStoreId == null) return false;
        return this.articleDisposition() === 'consumed' || this.targetUserId() != null || this.targetUserFromOperation();
      case 'technician_shipping':
        return this.targetUserId() != null || this.targetUserFromOperation();
      case 'customer_shipping':
        // Mit zugeordneten Staging-Artikeln nicht erlaubt (Staging laeuft nie
        // ueber Kunden-Rueckversand).
        if (this.stagingArticlesAssigned) return false;
        // Ziel-Kunde gewaehlt → der muss eine Ruecksendeadresse haben.
        if (this.targetCustomerId() != null) return this.selectedCustomerHasReturn();
        // Internes Projekt ohne Ziel-Kunden → Auswahl ist Pflicht.
        if (this.context?.project_is_internal) return false;
        // Sonst Fallback: Projekt-Kunde muss eine Ruecksendeadresse haben.
        return this.context?.customer_has_return_address === true;
      default: return false;
    }
  });

  // Abholzeit-Validierung gegen Aktivierungszeitraum + Wochentags-Oeffnungs-
  // zeiten der gewaehlten PickPoint-Station. Liefert eine Fehlermeldung oder
  // null. Wird im Template als Warnung angezeigt und gated `canSave`.
  readonly pickupValidityError = computed<string | null>(() => {
    if (this.selectedType() !== 'pickpoint') return null;
    const station = this.selectedStation();
    const parsed = parsePickupDateTime(this.pickupDate());
    if (!station || !parsed) return null;

    if (station.activation_date) {
      const act = parseIsoDateUtc(station.activation_date);
      if (act && parsed.date < act) return `Station erst ab ${formatDateDe(act)} aktiv.`;
    }
    if (station.cancellation_date) {
      const cancel = parseIsoDateUtc(station.cancellation_date);
      if (cancel && parsed.date >= cancel) return `Station ab ${formatDateDe(cancel)} nicht mehr aktiv.`;
    }

    const openings = station.openings ?? [];
    if (openings.length > 0) {
      const op = openings.find(o => o.day === parsed.day) ?? null;
      if (!op) return 'An diesem Wochentag keine Öffnungszeiten hinterlegt.';
      if (parsed.hhmm < op.open || parsed.hhmm > op.close) {
        return `Uhrzeit liegt außerhalb der Öffnungszeiten (${op.open}–${op.close}).`;
      }
    }
    return null;
  });

  // Anzeigetext fuer die Oeffnungszeiten am gewaehlten Tag (oder null).
  readonly pickupOpeningHint = computed<string | null>(() => {
    if (this.selectedType() !== 'pickpoint') return null;
    const station = this.selectedStation();
    const parsed = parsePickupDateTime(this.pickupDate());
    if (!station?.openings?.length || !parsed) return null;
    const op = station.openings.find(o => o.day === parsed.day);
    return op ? `Geöffnet ${op.open}–${op.close}` : null;
  });

  // Staging-Versand-Verzoegerung ist fuer alle Empfaengertypen mit Staging-
  // Bedarf relevant — nur beim reinen Kunden-Rueckversand (consumed) gibt es
  // keine Staging-Pakete.
  showStagingDelay = computed(() => this.selectedType() !== 'customer_shipping');

  // Wording der Offset-Regel je Versandtyp: PickPoint rechnet ab dem Abholtermin
  // des Abholauftrags (pickup_date), alle anderen Typen ab dem Auftragsdatum —
  // spiegelt ShippingConfig#rule_reference_date im Backend.
  readonly offsetWording = computed(() => this.selectedType() === 'pickpoint'
    ? { mode: 'X Werktage vor Abholung', unit: 'Werktage vor Abholtermin', noun: 'Abholtermin', day: 'Abholtag' }
    : { mode: 'X Werktage vor Auftrag', unit: 'Werktage vor Auftragsdatum', noun: 'Auftragsdatum', day: 'Auftragstag' });

  visibleShippingTypes = computed(() => this.shippingTypes);

  shippingTypes = [
    { value: 'pickpoint' as ShippingConfigType, label: 'PickPoint', icon: faMapMarkerAlt },
    { value: 'store_shipping' as ShippingConfigType, label: 'Filialversand', icon: faStore },
    { value: 'technician_shipping' as ShippingConfigType, label: 'Technikerversand', icon: faTruck },
    { value: 'customer_shipping' as ShippingConfigType, label: 'Kunden-Rückversand', icon: faUser },
  ];

  shipmentMethods = [
    { value: 'parcel' as ShipmentMethod, label: 'Paket', icon: faBox },
    { value: 'pallet' as ShipmentMethod, label: 'Palette', icon: faPallet },
    { value: 'courier' as ShipmentMethod, label: 'Kurier', icon: faMotorcycle },
    { value: 'pickup' as ShipmentMethod, label: 'Abholung', icon: faHandHolding },
  ];

  // PickPoint-Versand: Palette und Abholung sind nicht erlaubt — die Station
  // nimmt nur Pakete und Kurier-Sendungen entgegen.
  visibleShipmentMethods = computed(() => {
    if (this.selectedType() === 'pickpoint') {
      return this.shipmentMethods.filter((m) => m.value === 'parcel' || m.value === 'courier');
    }
    return this.shipmentMethods;
  });

  packageSpeeds = [
    { value: 'standard' as PackageSpeed, label: 'Standard', icon: faClock },
    { value: 'express' as PackageSpeed, label: 'Express', icon: faBolt },
  ];

  constructor(
    private userService: UserService,
    private shippingConfigService: ShippingConfigService,
    private customerService: CustomerService,
  ) {}

  // === Ziel-Kunde fuer Kunden-Rueckversand (v.a. interne Projekte) ===
  targetCustomerId = signal<number | null>(null);
  selectedCustomerName = signal<string>('');
  // Hat der gewaehlte Ziel-Kunde eine Ruecksendeadresse? Steuert canSave.
  selectedCustomerHasReturn = signal<boolean>(false);
  customerSearch = '';
  customerResults = signal<CustomerSearchResult[]>([]);
  showCustomerDropdown = signal<boolean>(false);

  onCustomerSearch(term: string): void {
    const q = term.trim();
    if (q.length < 2) {
      this.customerResults.set([]);
      this.showCustomerDropdown.set(false);
      return;
    }
    this.customerService.searchCustomers(q).subscribe({
      next: (results) => {
        this.customerResults.set(results);
        this.showCustomerDropdown.set(true);
      },
      error: (err) => console.error('Kundensuche fehlgeschlagen', err),
    });
  }

  selectCustomer(customer: CustomerSearchResult): void {
    this.targetCustomerId.set(customer.id);
    this.selectedCustomerName.set(customer.name);
    this.selectedCustomerHasReturn.set(customer.has_return_address);
    this.customerSearch = '';
    this.customerResults.set([]);
    this.showCustomerDropdown.set(false);
  }

  clearTargetCustomer(): void {
    this.targetCustomerId.set(null);
    this.selectedCustomerName.set('');
    this.selectedCustomerHasReturn.set(false);
  }

  ngOnInit(): void {
    this.applyInitialConfig();
    this.loadTechnicians();
    const uid = this.targetUserId();
    if (uid != null) {
      this.loadWarehouses(uid);
    }
    // PM-Code aus System-User (ID 1) vorbelegen, sofern PickPoint aktiv ist
    // und der Code noch leer ist (neue Config oder bestehende Config ohne
    // gesetzten PM-Code).
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) {
      this.loadMasterPmCode();
    }
    // Erweiterte Einstellungen sind grundsaetzlich eingeklappt — der User
    // klickt aktiv darauf, wenn er sie braucht.
  }

  private async loadMasterPmCode(): Promise<void> {
    const code = await this.shippingConfigService.getMasterPmCode();
    if (code) {
      this.pmCode.set(code);
      this.pmCodeAutoLoaded.set(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialConfig'] && !changes['initialConfig'].firstChange) {
      this.applyInitialConfig();
      const uid = this.targetUserId();
      if (uid != null) {
        this.loadWarehouses(uid);
      }
    }
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      // Techniker-Liste neu laden (Projekt-Filter)
      this.technicians.set([]);
      this.loadTechnicians();
    }
    // Context (Auftragsgruppe + Default-Offset) trifft oft erst nach dem ersten
    // Render ein — dann die Regel-Vorlaufzeit nachziehen, solange der User den
    // Offset nicht selbst gesetzt/getippt hat:
    //  - Create + Offset-Modus (Default-Anlage), oder
    //  - Edit OHNE gespeicherten Offset, solange noch im Fest-Datum-Modus
    //    (das Offset-Feld ist dann verborgen → gefahrlos den latenten Default
    //    aktualisieren, damit ein spaeteres Umschalten den richtigen Wert zeigt).
    if (changes['context']) {
      const noStoredOffset = this.initialConfig?.shipping_date_offset_days == null;
      const createOffset = !this.initialConfig && this.shippingDateMode() === 'offset';
      const editLatent = !!this.initialConfig && noStoredOffset && this.shippingDateMode() === 'fixed';
      if (createOffset || editLatent) {
        this.shippingOffsetDays.set(this.defaultShippingOffsetDays());
      }
      // Interner Materialversand: festen Ziel-Techniker aus dem Filialnamen
      // vorbelegen (Context trifft i.d.R. erst nach dem ersten Render ein).
      this.applyInternalStoreTechnician();
    }
  }

  isTypeDisabled(type: ShippingConfigType): boolean {
    if (type === 'store_shipping' && this.ticketStoreId == null) return true;
    if (type === 'customer_shipping' && this.stagingArticlesAssigned) return true;
    return false;
  }

  typeDisabledReason(type: ShippingConfigType): string | null {
    if (type === 'store_shipping' && this.ticketStoreId == null) {
      return 'Nur mit Ticketfiliale möglich';
    }
    if (type === 'customer_shipping' && this.stagingArticlesAssigned) {
      return 'Kundenrückversand nicht auswählbar, solange Staging Artikel zugeordnet sind';
    }
    return null;
  }

  // Einmalig angewandt: interner-Materialversand-Default (fester Techniker aus
  // dem Ticketfiliale-Namen). Verhindert, dass eine spaetere Context-Aenderung
  // eine bereits getroffene Userwahl ueberschreibt.
  private internalTechApplied = false;

  // Interner Materialversand: ist im Context ein ueber den Filialnamen
  // aufgeloester Techniker hinterlegt, beim NEU-Anlegen einen FESTEN Ziel-
  // Techniker vorbelegen (statt der Auftrags-Regel) — aber nur, solange der User
  // noch nichts Eigenes gewaehlt hat (Regel-Modus ohne festen Techniker).
  private applyInternalStoreTechnician(): void {
    if (this.initialConfig || this.internalTechApplied) return;
    const id = this.context?.internal_store_technician_id;
    if (id == null) return;
    if (!this.targetUserFromOperation() || this.targetUserId() != null) return;

    this.internalTechApplied = true;
    this.ensureMatchedTechnicianOption();
    this.targetUserFromOperation.set(false);
    this.onTechnicianChange(id);
  }

  // Stellt sicher, dass der ueber den Filialnamen aufgeloeste Techniker im
  // Dropdown auswaehlbar ist (er ist evtl. kein Projekt-Techniker und fehlt
  // dann in der normalen Liste).
  private ensureMatchedTechnicianOption(): void {
    const id = this.context?.internal_store_technician_id;
    const name = this.context?.internal_store_technician_name;
    if (id == null || !name) return;
    if (this.technicians().some(t => t.id === id)) return;
    this.technicians.set(
      [...this.technicians(), { id, name }].sort((a, b) => a.name.localeCompare(b.name)),
    );
  }

  // Umschalten Techniker-Regel (vom Auftrag) ↔ fester Techniker. Beim Wechsel in
  // den Regel-Modus den festen Techniker/Lager verwerfen.
  toggleTargetUserFromOperation(on: boolean): void {
    this.targetUserFromOperation.set(on);
    if (on) {
      this.targetUserId.set(null);
      this.targetWarehouseId = null;
      this.technicianWarehouses.set([]);
    }
  }

  onTechnicianChange(userId: number | null): void {
    this.targetUserId.set(userId);
    this.targetWarehouseId = null;
    if (userId != null) {
      this.loadWarehouses(userId);
    } else {
      this.technicianWarehouses.set([]);
    }
  }

  setShipmentMethod(method: ShipmentMethod): void {
    this.shipmentMethod.set(method);
    if (method === 'parcel') {
      if (this.packageSpeed() == null) this.packageSpeed.set('standard');
    } else {
      this.packageSpeed.set(null);
    }
  }

  // Wechselt den Versandtyp und setzt unzulaessige Versandarten zurueck —
  // PickPoint erlaubt nur Paket und Kurier; bei Wechsel von z.B. pallet
  // auf pickpoint waere shipmentMethod sonst inkonsistent.
  // Beim Wechsel zu PickPoint wird ausserdem der Master-PM-Code geladen,
  // sofern das Feld leer ist (z.B. weil vorher eine andere Config-Variante
  // ohne PM-Code aktiv war).
  setShippingType(type: ShippingConfigType): void {
    if (this.isTypeDisabled(type)) return;
    const previousType = this.selectedType();
    this.selectedType.set(type);
    if (type === 'pickpoint') {
      const m = this.shipmentMethod();
      if (m !== 'parcel' && m !== 'courier') {
        this.setShipmentMethod('parcel');
      }
      if (!this.pmCode().trim()) {
        this.loadMasterPmCode();
      }
      // Wechsel auf PickPoint: Default-Pickup-Vorschlag einblenden, falls
      // das Feld leer ist (z.B. weil vorher Filial- oder Technikerversand
      // aktiv war). Muss VOR applyShippingDefaultsForType passieren.
      if (!this.pickupDate()) {
        this.pickupDate.set(this.defaultPickupDate());
      }
    }
    // Bei echtem Typ-Wechsel Versand-/Staging-Datum nach dem Typ-Schema neu
    // setzen: PickPoint → beide 5 Tage vor Abholzeitpunkt; sonst Versanddatum
    // 14 Tage vor Auftragsdatum, Staging-Verzoegerung leer. Re-Klick auf den
    // bereits aktiven Typ aendert nichts (keine versehentliche Ueberschreibung).
    if (previousType !== type) {
      this.applyShippingDefaultsForType(type);
    }
  }

  onMapStationPicked(station: PickpointStation | null): void {
    if (!station) return;
    this.selectedStation.set({
      id: station.id,
      pp_id: station.pp_id,
      name: station.name,
      address_zip: station.address_zip,
      address_city: station.address_city,
      active: station.active,
      // Fuer die Abholzeit-Validierung weiterreichen — beim Edit-Flow kommen
      // dieselben Felder aus dem ShippingConfig-Hash (siehe Backend).
      openings: station.openings,
      activation_date: station.activation_date,
      cancellation_date: station.cancellation_date,
    });
  }

  emitSave(): void {
    const type = this.selectedType();
    const disposition = this.defaultDispositionForType(type, this.articleDisposition());

    // Datums-Regel: im Offset-Modus die "X Werktage vorher"-Regel schicken (und
    // das feste Datum verwerfen), sonst das feste Datum. Felder immer mitgeben
    // (auch null), damit ein Wechsel zurueck auf festes Datum die Regel loescht.
    const useShippingOffset = this.shippingDateMode() === 'offset';
    const stagingRelevant = type !== 'customer_shipping';
    const useStagingOffset = stagingRelevant && this.stagingShipMode() === 'offset';
    // 'standard' = keine Verzoegerung → weder fester Termin noch Offset.
    const useStagingFixed = stagingRelevant && this.stagingShipMode() === 'fixed';

    const data: ShippingConfigFormData = {
      shipping_type: type,
      shipment_method: this.shipmentMethod()!,
      package_speed: this.shipmentMethod() === 'parcel' ? this.packageSpeed() : null,
      article_disposition: disposition,
      shipping_date: useShippingOffset ? null : (this.shippingDate() || null),
      shipping_date_offset_days: useShippingOffset ? Number(this.shippingOffsetDays()) : null,
      staging_ship_ready_at: useStagingFixed ? (this.stagingShipReadyAt() || null) : null,
      staging_ship_offset_days: useStagingOffset ? Number(this.stagingOffsetDays()) : null,
      pm_code: type === 'pickpoint' ? this.pmCode().trim() : null,
      pickup_date: type === 'pickpoint' ? this.pickupDate() || null : null,
    };

    // Techniker-Regel: im Regel-Modus keinen festen Techniker/Lager mitschicken.
    const ruleTech = this.showTechnicianSelector() && this.targetUserFromOperation();

    switch (type) {
      case 'pickpoint':
        data.pickpoint_station_id = this.selectedStation()!.id;
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTech ? null : this.targetUserId();
        data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        break;
      case 'store_shipping':
        if (disposition === 'consumed') {
          data.target_user_id = null;
          data.target_warehouse_id = null;
        } else {
          data.target_user_from_operation = this.targetUserFromOperation();
          data.target_user_id = ruleTech ? null : this.targetUserId();
          data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        }
        break;
      case 'technician_shipping':
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTech ? null : this.targetUserId();
        data.target_warehouse_id = ruleTech ? null : this.targetWarehouseId;
        data.technician_ship_target = this.technicianShipTarget();
        break;
      case 'customer_shipping':
        // Optionaler Ziel-Kunde (Pflicht bei internen Projekten) — leer =
        // Fallback auf den Projekt-Kunden im Backend.
        data.target_customer_id = this.targetCustomerId();
        break;
    }

    this.save.emit(data);
  }

  private applyInitialConfig(): void {
    const c = this.initialConfig;
    if (c) {
      this.selectedType.set(c.shipping_type);
      this.selectedStation.set(c.pickpoint_station ?? null);
      // Beim Bearbeiten exakt den DB-Stand uebernehmen — KEIN Fallback auf den
      // Auftragstechniker, sonst sieht es so aus, als waere er gespeichert.
      // Auto-Configs (z.B. fuer interne Auftraege) duerfen ja explizit ohne
      // festen Techniker existieren.
      this.targetUserId.set(c.target_user_id ?? null);
      this.targetWarehouseId = c.target_warehouse_id ?? null;
      // Regel "Techniker vom Auftrag" aus dem DB-Stand uebernehmen.
      this.targetUserFromOperation.set(c.target_user_from_operation ?? false);
      // Datums-Modus aus dem DB-Stand: gesetzter Offset → Regel-Modus, sonst
      // festes Datum.
      if (c.shipping_date_offset_days != null) {
        this.shippingDateMode.set('offset');
        this.shippingOffsetDays.set(c.shipping_date_offset_days);
      } else {
        this.shippingDateMode.set('fixed');
        // Kein gespeicherter Offset: das (noch verborgene) Offset-Feld mit dem
        // Gruppen-Default aus dem Context vorbelegen, damit ein Umschalten auf
        // "X Werktage vor Auftrag" den korrekten Default zeigt — nicht den
        // Signal-Initialwert.
        this.shippingOffsetDays.set(this.defaultShippingOffsetDays());
      }
      // Staging-Modus aus dem DB-Stand (3 Modi): gesetzter Offset → 'offset';
      // gesetzter fester Termin (oder aktive PickPoint-Config ohne Termin → -5-WT-
      // Default, s. ready_at-Block unten) → 'fixed'; sonst keine Verzoegerung →
      // 'standard' (Versand nach KW-Zuordnung).
      if (c.staging_ship_offset_days != null) {
        this.stagingShipMode.set('offset');
        this.stagingOffsetDays.set(c.staging_ship_offset_days);
      } else if (c.staging_ship_ready_at) {
        this.stagingShipMode.set('fixed');
      } else if (c.shipping_type === 'pickpoint' && !c.shipped_at) {
        // Aktive PickPoint-Config ohne gespeicherte Staging-Verzoegerung:
        // Offset-Default 5 WT (einheitlich mit der Neuanlage).
        this.stagingShipMode.set('offset');
        this.stagingOffsetDays.set(this.STAGING_DELAY_DAYS);
      } else {
        this.stagingShipMode.set('standard');
      }
      // Techniker-Versandadresse aus dem DB-Stand; ohne gesetzten Wert Default
      // 'warehouse' (Lageradresse).
      this.technicianShipTarget.set(c.technician_ship_target ?? 'warehouse');
      // Ziel-Kunde fuer Kunden-Rueckversand aus dem DB-Stand. Wurde beim Speichern
      // gegen seine Ruecksendeadresse validiert → als vorhanden behandeln.
      this.targetCustomerId.set(c.target_customer_id ?? null);
      this.selectedCustomerName.set(c.target_customer_name ?? '');
      this.selectedCustomerHasReturn.set(c.target_customer_id != null);
      this.shipmentMethod.set(c.shipment_method ?? 'parcel');
      this.packageSpeed.set(c.shipment_method === 'parcel' ? (c.package_speed ?? 'standard') : null);
      this.articleDisposition.set(c.article_disposition ?? this.defaultDispositionForType(c.shipping_type));
      this.shippingDate.set(c.shipping_date ?? '');
      this.pmCode.set(c.pm_code ?? '');
      // Backend liefert ISO-8601 mit Sekunden/Zone — datetime-local braucht
      // "YYYY-MM-DDTHH:mm". Bei bestehender Config nehmen wir den DB-Wert;
      // bei einer PickPoint-Config ohne pickup_date (z.B. Type-Wechsel von
      // store_shipping → pickpoint) fallen wir auf den Default-Vorschlag
      // (operation_date - 1h) zurueck, damit das Feld nicht leer bleibt.
      const pickupFromDb = c.pickup_date ? this.toDatetimeLocal(c.pickup_date) : '';
      this.pickupDate.set(pickupFromDb || (c.shipping_type === 'pickpoint' ? this.defaultPickupDate() : ''));
      // Verzoegerung: gesetzten DB-Wert exakt uebernehmen. Ist sie bei einer
      // AKTIVEN PickPoint-Config noch leer, den −5-Tage-Vorschlag vorbefuellen
      // (PickPoint soll standardmaessig verzoegert versandt werden — gilt auch
      // beim Bearbeiten frisch auto-generierter Configs, z.B. im Sonderversand).
      if (c.staging_ship_ready_at) {
        this.stagingShipReadyAt.set(this.toDatetimeLocal(c.staging_ship_ready_at));
      } else if (c.shipping_type === 'pickpoint' && !c.shipped_at) {
        this.stagingShipReadyAt.set(this.computeStagingShipReady());
      } else {
        this.stagingShipReadyAt.set('');
      }
    } else {
      this.selectedType.set('pickpoint');
      this.selectedStation.set(null);
      // Auftragstechniker vorbelegen — aber Platzhalter-User (Lager / nicht
      // vergeben) nicht uebernehmen (analog Backend-Auto-Create).
      const opTech = this.operationTechnicianId ?? null;
      this.targetUserId.set(
        opTech != null && ShippingConfigFormComponent.PLACEHOLDER_TECHNICIAN_IDS.includes(opTech) ? null : opTech,
      );
      this.targetWarehouseId = null;
      // Regel als Standard: neuer Auftrag → Ziel-Techniker dynamisch vom Auftrag.
      this.targetUserFromOperation.set(true);
      this.technicianShipTarget.set('warehouse');
      this.clearTargetCustomer();
      this.shipmentMethod.set('parcel');
      this.packageSpeed.set('standard');
      this.articleDisposition.set('technician_transfer');
      this.pmCode.set('');
      this.pickupDate.set(this.defaultPickupDate());
      // Versand- + Staging-Datum nach Typ-Schema vorbefuellen (Default-Typ
      // PickPoint → beide 5 Tage vor Abholzeitpunkt). pickupDate muss vorher
      // gesetzt sein, weil der PickPoint-Vorschlag darauf aufbaut.
      this.applyShippingDefaultsForType(this.selectedType());
    }
  }

  // Berechnet den Vorschlag fuer den frueheste-Versand-Zeitpunkt: Bezug ist der
  // Abholzeitpunkt (pickup_date) — denn an dem Tag wird der Abholauftrag fuer
  // den Techniker generiert; faellt er leer, das Auftragsdatum (referenceDate,
  // an dem dann ebenfalls der Abholauftrag entsteht). Minus STAGING_DELAY_DAYS,
  // Uhrzeit auf 09:00. Liefert "" wenn kein Bezug ermittelbar ist.
  private computeStagingShipReady(): string {
    const basis = this.pickupDate() || this.referenceDate || '';
    if (!basis) return '';
    const d = new Date(basis);
    if (Number.isNaN(d.getTime())) return '';
    this.subtractWorkingDays(d, this.STAGING_DELAY_DAYS);
    d.setHours(9, 0, 0, 0);
    return this.toDatetimeLocalFromDate(d);
  }

  // Zieht `n` Werktage ab — Sonntage zaehlen NICHT als Werktag und werden
  // uebersprungen (Samstag gilt als Werktag, damit die Verschiebung effektiv
  // ~5 Tage bleibt und nicht eine ganze Woche). Faellt das ERGEBNIS auf einen
  // Samstag, wird es auf Freitag vorgezogen (am Samstag versendet im Lager
  // niemand). Feiertage werden bewusst noch nicht beruecksichtigt. Mutiert `d`.
  private subtractWorkingDays(d: Date, n: number): void {
    let remaining = n;
    while (remaining > 0) {
      d.setDate(d.getDate() - 1);
      if (d.getDay() !== 0) remaining -= 1; // 0 = Sonntag
    }
    // Ergebnis auf Samstag → auf Freitag vorziehen.
    if (d.getDay() === 6) d.setDate(d.getDate() - 1);
  }

  // Default fuers „normale" Versanddatum (type="date" → "YYYY-MM-DD") nach Typ:
  //  - PickPoint: 5 Tage vor dem Abholzeitpunkt (pickup_date, sonst referenceDate)
  //  - sonst:     14 Tage vor dem Auftragsdatum (referenceDate) — ABER nur bei
  //    EXTERNEN Aufträgen OHNE Material-Versand-Prio. Bei internen Aufträgen und
  //    bei externen mit material_shipping-Prio bleibt das Feld leer (= Versand am
  //    Auftragsdatum), da dort kein Vorlauf gebraucht wird.
  // Liefert "" wenn kein Bezugsdatum ermittelbar ist (Feld bleibt leer).
  private shipDateDefaultForType(type: ShippingConfigType): string {
    if (type === 'pickpoint') {
      // PickPoint: 5 Werktage vor dem Abholzeitpunkt (Sonntage rausgerechnet).
      const basis = this.pickupDate() || this.referenceDate || '';
      if (!basis) return '';
      const d = new Date(basis);
      if (Number.isNaN(d.getTime())) return '';
      this.subtractWorkingDays(d, this.STAGING_DELAY_DAYS);
      return this.formatLocalDate(d);
    }

    const isInternal = this.context?.project_is_internal === true;
    const hasMaterialShippingPriority = this.context?.ticket_has_material_shipping_priority === true;
    if (isInternal || hasMaterialShippingPriority) return '';

    return this.dateLocalMinusDays(this.referenceDate || '', this.DEFAULT_LEAD_DAYS);
  }

  private dateLocalMinusDays(basis: string, days: number): string {
    if (!basis) return '';
    const d = new Date(basis);
    if (Number.isNaN(d.getTime())) return '';
    d.setDate(d.getDate() - days);
    return this.formatLocalDate(d);
  }

  private formatLocalDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // Setzt Versanddatum + Staging-Verzoegerung nach dem Typ-Schema. PickPoint:
  // beide 5 Tage vor Abholzeitpunkt. Andere Typen: Versanddatum 14 Tage vor
  // Auftragsdatum, Staging-Verzoegerung leer (Delay brauchen wir im Hauptfall
  // nur bei PickPoint). Gilt fuer Neu-Anlage UND Typ-Wechsel.
  private applyShippingDefaultsForType(type: ShippingConfigType): void {
    // Normaler Versand: Regel als Standard ("X Werktage vor Auftragsdatum") mit
    // dem Gruppen-Default aus dem Context. shippingDate als Fallback fuer den
    // Fest-Modus weiterhin vorbelegen.
    this.shippingDateMode.set('offset');
    this.shippingOffsetDays.set(this.defaultShippingOffsetDays());
    this.shippingDate.set(this.shipDateDefaultForType(type));
    // Staging: PickPoint bekommt als Default die Offset-Regel "X Werktage vor
    // Auftrag" mit 5 WT (einheitlich mit dem normalen Versand, statt des frueheren
    // festen -5-WT-Termins). Sonst Standard (keine Verzoegerung, Versand nach
    // KW-Zuordnung). stagingShipReadyAt als Vorschlag fuer den Fest-Modus weiter
    // vorbelegen, falls der User umschaltet.
    if (type === 'pickpoint') {
      this.stagingShipMode.set('offset');
      this.stagingOffsetDays.set(this.STAGING_DELAY_DAYS);
      this.stagingShipReadyAt.set(this.computeStagingShipReady());
    } else {
      this.stagingShipMode.set('standard');
      this.stagingShipReadyAt.set('');
    }
  }

  // Default-Vorlaufzeit (Werktage) fuer die "X Werktage vorher"-Regel des
  // normalen Versands. Quelle: Auftragsgruppe (Context). Fallback, falls der
  // Context noch nicht geladen ist: der bisherige 14-Tage-Default.
  private defaultShippingOffsetDays(): number {
    // PickPoint-Ausnahme: 5 Werktage (Bezug ist konzeptionell der Abholtermin,
    // der ~ am Auftragsdatum liegt) — analog zum Staging-Default.
    if (this.selectedType() === 'pickpoint') return this.STAGING_DELAY_DAYS;

    const v = this.context?.default_shipping_offset_days;
    return v != null ? v : this.DEFAULT_OFFSET_WORKING_DAYS;
  }

  // Berechnet den Default-Pickup-Vorschlag (operation_date - 1h) aus dem
  // referenceDate-Input. Liefert "" wenn das referenceDate fehlt oder nicht
  // parsebar ist — dann bleibt das Feld leer und das Backend setzt beim
  // Anlegen einen Default auf Basis von operation.operation_date.
  private defaultPickupDate(): string {
    if (!this.referenceDate) return '';
    const ref = new Date(this.referenceDate);
    if (Number.isNaN(ref.getTime())) return '';
    ref.setHours(ref.getHours() - 1);
    return this.toDatetimeLocalFromDate(ref);
  }

  // Wandelt einen ISO-8601-String in das von <input type="datetime-local">
  // erwartete Format "YYYY-MM-DDTHH:mm" in lokaler Zeitzone um. Direktes
  // Abschneiden auf 16 Zeichen funktioniert nur, wenn der String bereits
  // ohne Zonen-Offset in lokaler Zeit ist — andernfalls verschiebt sich
  // der Wert um die TZ-Differenz.
  private toDatetimeLocal(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return this.toDatetimeLocalFromDate(d);
  }

  private toDatetimeLocalFromDate(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  private loadTechnicians(): void {
    if (this.technicians().length > 0) return;
    this.userService.getTechnicians(this.projectId ? { project_id: this.projectId } : {}).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || [])
          // Platzhalter-/System-User (Bentomax Lager, nicht vergeben) nicht als
          // Ziel-Techniker waehlbar — gespiegelt zur Backend-Validierung.
          .filter(u => !ShippingConfigFormComponent.PLACEHOLDER_TECHNICIAN_IDS.includes(u.id))
          .map(u => ({
            id: u.id,
            name: u.full_name || u.name || `#${u.id}`,
          } as TechnicianOption));
        mapped.sort((a, b) => a.name.localeCompare(b.name));
        this.technicians.set(mapped);
        // Den ggf. ueber den Filialnamen aufgeloesten Techniker (interner
        // Materialversand) sicher in die Liste aufnehmen — er ist evtl. kein
        // Projekt-Techniker und fehlt sonst im Dropdown.
        this.ensureMatchedTechnicianOption();
      },
      error: err => console.error('Techniker konnten nicht geladen werden', err),
    });
  }

  private loadWarehouses(userId: number): void {
    this.loadingWarehouses.set(true);
    this.userService.getUsersWarehouses(userId).subscribe({
      next: (data: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const mapped = (data || []).map(w => ({ id: w.id, label: w.label } as WarehouseOption));
        this.technicianWarehouses.set(mapped);

        const current = this.targetWarehouseId;
        if (current != null && !mapped.some(w => w.id === current)) {
          this.targetWarehouseId = mapped[0]?.id ?? null;
        } else if (current == null && mapped.length === 1) {
          this.targetWarehouseId = mapped[0].id;
        }
        this.loadingWarehouses.set(false);
      },
      error: err => {
        console.error('Lager konnten nicht geladen werden', err);
        this.technicianWarehouses.set([]);
        this.loadingWarehouses.set(false);
      },
    });
  }

  private defaultDispositionForType(
    type: ShippingConfigType,
    userChoice?: ArticleDisposition,
  ): ArticleDisposition {
    switch (type) {
      case 'customer_shipping': return 'consumed';
      case 'pickpoint':
      case 'technician_shipping': return 'technician_transfer';
      case 'store_shipping': return userChoice ?? 'technician_transfer';
    }
  }
}

// datetime-local-Wert ("YYYY-MM-DDTHH:mm") in Datum (UTC-Mitternacht) +
// Wochentag (0=Mo..6=So) + "HH:mm" zerlegen. Liefert null bei leerem/
// ungueltigem Input. UTC-Mitternacht macht den Datums-Vergleich gegen
// activation_date / cancellation_date zeitzonen-stabil.
function parsePickupDateTime(raw: string): { date: Date; day: number; hhmm: string } | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(raw);
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const day = (date.getUTCDay() + 6) % 7;
  return { date, day, hhmm: `${m[4]}:${m[5]}` };
}

function parseIsoDateUtc(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function formatDateDe(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}
